"use client";
import { Dispatch, RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@repo/web-ui/lib/utils";
import { useResizeObserver } from 'usehooks-ts'

interface Props {
  className?: string;
  defaultValue?: number; //value in radians
  value: number | undefined; //value in radians
  onValueChange: (value: number) => void; //value in radians
  onValueCommit?: (value: number) => void; //value in radians
  zeroOffest?: number; //radians to offset zero location
  limits?: { startAngle: number; sweepAngle: number }; //in radians
  labels?: string[];
  trackThickness?: number;
  trackLinecap?: "inherit" | "round" | "butt" | "square" | undefined;
  handleSize?: number;
  onClickOutside?: (e: MouseEvent | TouchEvent) => void
}
const CircleSlider = ({
  className = "",
  defaultValue = 0,
  value = defaultValue,
  onValueChange,
  onValueCommit,
  zeroOffest = 0,
  limits = { startAngle: 0, sweepAngle: 2*Math.PI/2 },
  labels = [],
  trackThickness = 6,
  trackLinecap = "round",
  handleSize = 16,
  onClickOutside
}: Props) => {
  const parentContainerRef = useRef<HTMLDivElement>(null!)
  const parentContainerElement = useResizeObserver({ref: parentContainerRef})
  const childContainerRef = useRef<HTMLDivElement>(null!)
  const trackRef = useRef<SVGCircleElement | SVGPathElement>(null!)
  const handleRef = useRef<SVGSVGElement>(null!)
  const diameter = useMemo(() => {
    const width = parentContainerElement.width ?? 0
    const height = parentContainerElement.width ?? 0
    return Math.min(width, height);
  }, [parentContainerElement]);
  const [isDragging, setIsDragging] = useState(false)


  const onMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging && !!childContainerRef.current) {
        e.preventDefault()
        const bounds = childContainerRef.current.getBoundingClientRect();
      const center = {
        x: bounds.left + diameter / 2,
        y: bounds.top + diameter / 2,
      };
      const angle =  Math.atan2(e.clientY - center.y, e.clientX - center.x) - zeroOffest;
      if (isAngleOnArc(angle, limits.startAngle, limits.sweepAngle)) {
        onValueChange(normalizeAngle(angle))
      }
    }
}, [isDragging])

const onTouchMove = useCallback((e: TouchEvent) => {
    if (isDragging && !!childContainerRef.current && e.touches[0]) {
        e.preventDefault()
        const bounds = childContainerRef.current.getBoundingClientRect();
        const center = {
            x: bounds.left + diameter / 2,
            y: bounds.top + diameter / 2,
        };
        const angle =
            Math.atan2(
            e.touches[0].clientY - center.y,
            e.touches[0].clientX - center.x
            ) - zeroOffest;
        if (isAngleOnArc(angle, limits.startAngle, limits.sweepAngle)) {
          onValueChange(normalizeAngle(angle))
        }
    }
}, [isDragging])

const onMouseUp = useCallback((e: MouseEvent) => {
  if (onValueCommit && isDragging && !!childContainerRef.current) {
      e.preventDefault()
      const bounds = childContainerRef.current.getBoundingClientRect();
    const center = {
      x: bounds.left + diameter / 2,
      y: bounds.top + diameter / 2,
    };
    const angle =  Math.atan2(e.clientY - center.y, e.clientX - center.x) - zeroOffest;
    if (isAngleOnArc(angle, limits.startAngle, limits.sweepAngle)) {
      onValueCommit(normalizeAngle(angle))
    } else {
      // return the limit value closest to the mouseup event
      onValueCommit(normalizeAngle(getClosestLimitAngle(angle, limits.startAngle, limits.startAngle + limits.sweepAngle)))
    }
  }
  setIsDragging(false)
}, [isDragging])
const onTouchEnd = useCallback((e: TouchEvent) => {
  console.log('onTouchEndCalled', onValueChange, isDragging, !!childContainerRef.current, e.changedTouches[0])
  if (onValueCommit && isDragging && !!childContainerRef.current && e.changedTouches[0]) {
      e.preventDefault()
      const bounds = childContainerRef.current.getBoundingClientRect();
      const center = {
          x: bounds.left + diameter / 2,
          y: bounds.top + diameter / 2,
      };
      const angle =
          Math.atan2(
          e.changedTouches[0].clientY - center.y,
          e.changedTouches[0].clientX - center.x
          ) - zeroOffest;
      if (isAngleOnArc(angle, limits.startAngle, limits.sweepAngle)) {
        onValueCommit(normalizeAngle(angle))
      } else {
        // return the limit value closest to the mouseup event
        onValueCommit(normalizeAngle(getClosestLimitAngle(angle, limits.startAngle, limits.startAngle + limits.sweepAngle)))
      }
  }
  setIsDragging(false)
}, [isDragging])

// handle click/touch outside of track/handle
const onMouseDown = (e: MouseEvent) => {
  if (
    onClickOutside &&
    trackRef.current && !trackRef.current.contains(e.target as Node) &&
    handleRef.current && !handleRef.current.contains(e.target as Node)
  ) {
    onClickOutside(e)
  }
}
const onTouchStart = (e: TouchEvent) => {
  if (
    onClickOutside &&
    trackRef.current && !trackRef.current.contains(e.target as Node) &&
    handleRef.current && !handleRef.current.contains(e.target as Node)
  ) {
    onClickOutside(e)
  }
}
useEffect(() => {
  window.addEventListener('mousedown', onMouseDown)
  window.addEventListener('touchstart', onTouchStart)

  return () => {
    window.removeEventListener('mousedown', onMouseDown)
    window.removeEventListener('touchstart', onTouchStart)
  }
},[])

  useEffect(() => {
    if (isDragging) {
        window.addEventListener('mousemove', onMouseMove)
        window.addEventListener('touchmove', onTouchMove, { passive: false })
        window.addEventListener('mouseup', onMouseUp)
        window.addEventListener('touchend', onTouchEnd, { passive: false })
    }
    return () => {
        window.removeEventListener('mousemove', onMouseMove)
        window.removeEventListener('touchmove', onTouchMove)
        window.removeEventListener('mouseup', onMouseUp)
        window.removeEventListener('touchend', onTouchEnd)
    }
  },[isDragging])

  return (
    <div
      ref={parentContainerRef}
      className={cn(
        "relative flex justify-center items-center w-full h-full",
        className
      )}
    >
      <div ref={childContainerRef} style={{ width: diameter, height: diameter }} className="relative">
        <Track
          childContainerRef={childContainerRef}
          trackRef={trackRef}
          diameter={diameter}
          trackThickness={trackThickness}
          zeroOffest={zeroOffest}
          limits={limits}
          trackLinecap={trackLinecap}
          labels={labels}
          className={className}
          setIsDragging={setIsDragging}
          onValueChange={onValueChange}
        />
        <Handle
          handleRef={handleRef}
          diameter={diameter ?? 100}
          trackThickness={trackThickness}
          zeroOffest={zeroOffest}
          value={value}
          handleSize={handleSize}
          className={className}
          isDragging={isDragging}
          setIsDragging={setIsDragging}
        />
      </div>
    </div>
  );
};

export default CircleSlider;

interface TrackProps {
  childContainerRef: RefObject<HTMLDivElement>;
  trackRef: RefObject<SVGCircleElement | SVGPathElement>;
  diameter: number;
  trackThickness: number;
  zeroOffest: number;
  limits: { startAngle: number; sweepAngle: number }; //in radians
  trackLinecap: "inherit" | "round" | "butt" | "square" | undefined;
  labels: string[];
  className: string;
  setIsDragging: Dispatch<React.SetStateAction<boolean>>
  onValueChange: (value: number) => void;
}
const Track = ({
  childContainerRef,
  trackRef,
  diameter,
  trackThickness,
  zeroOffest,
  limits,
  trackLinecap,
  labels,
  className,
  setIsDragging,
  onValueChange
}: TrackProps) => {
  const labelMap: {
    label: string;
    x: number;
    y: number;
    visible: boolean;
  }[] = useMemo(() => {
    const angleStep = (2 * Math.PI) / labels.length;
    return labels.map((label, index) => {
      const angle = index * angleStep + zeroOffest;
      const visible =
        isAngleOnArc(angle, limits.startAngle + zeroOffest, limits.sweepAngle)
      const x =
        diameter / 2 + (diameter / 2 - trackThickness) * Math.cos(angle);
      const y =
        diameter / 2 + (diameter / 2 - trackThickness) * Math.sin(angle);
      return { label: label, x: x, y: y, visible: visible };
    });
  }, [labels, zeroOffest, limits, diameter, trackThickness]);

  const onMouseDown = (
    e: React.MouseEvent<SVGCircleElement | SVGPathElement, MouseEvent>
  ) => {
    if (childContainerRef.current) {
      const bounds = childContainerRef.current.getBoundingClientRect();
      const center = {
        x: bounds.left + diameter / 2,
        y: bounds.top + diameter / 2,
      };
      const angle =  Math.atan2(e.clientY - center.y, e.clientX - center.x) - zeroOffest;
      if (isAngleOnArc(angle, limits.startAngle, limits.sweepAngle)) {
        setIsDragging(true)
        onValueChange(normalizeAngle(angle))
      }
    }
  };
  const onTouchStart = (
    e: React.TouchEvent<SVGCircleElement | SVGPathElement>
  ) => {
    if (e.touches[0] && childContainerRef.current) {
      const bounds = childContainerRef.current.getBoundingClientRect();
      const center = {
        x: bounds.left + diameter / 2,
        y: bounds.top + diameter / 2,
      };
      const angle =
        Math.atan2(
          e.touches[0].clientY - center.y,
          e.touches[0].clientX - center.x
        ) - zeroOffest;
      if (isAngleOnArc(angle, limits.startAngle, limits.sweepAngle)) {
        setIsDragging(true)
        onValueChange(normalizeAngle(angle))
      }
    }
  };

  const cos = Math.cos;
  const sin = Math.sin;
  const π = Math.PI;
  const f_matrix_times = (
    [[a, b], [c, d]]: [[number, number], [number, number]],
    [x, y]: [number, number]
  ): [number, number] => [a * x + b * y, c * x + d * y];
  const f_rotate_matrix = (x: number): [[number, number], [number, number]] => [
    [cos(x), -sin(x)],
    [sin(x), cos(x)],
  ];
  const f_vec_add = (
    [a1, a2]: [number, number],
    [b1, b2]: [number, number]
  ): [number, number] => [a1 + b1, a2 + b2];

  const f_svg_ellipse_arc = (
    [cx, cy]: [number, number],
    [rx, ry]: [number, number],
    [t1, Δ]: [number, number],
    φ: number
  ) => {
    Δ = Δ % (2 * π);
    const rotMatrix = f_rotate_matrix(φ);

    const [sX, sY] = f_vec_add(
      f_matrix_times(rotMatrix, [rx * cos(t1), ry * sin(t1)]),
      [cx, cy]
    );

    const [eX, eY] = f_vec_add(
      f_matrix_times(rotMatrix, [rx * cos(t1 + Δ), ry * sin(t1 + Δ)]),
      [cx, cy]
    );

    const fA = Δ > π ? 1 : 0;
    const fS = Δ > 0 ? 1 : 0;
    return { sX, sY, rx, ry, fA, fS, eX, eY };
  };
  /*
    returns a SVG path element that represents an ellipse.
    cx, cy → center of ellipse
    rx, ry → major minor radius
    t1 → start angle, in radian.
    Δ → angle to sweep, in radian. positive.
    φ → rotation on the whole, in radian
    */
  const arcPath = useMemo(() => {
    return f_svg_ellipse_arc(
      [diameter / 2, diameter / 2], //center of circle
      [diameter / 2 - trackThickness, diameter / 2 - trackThickness], //radius
      [limits.startAngle, limits.sweepAngle],
      zeroOffest
    );
  }, [diameter, trackThickness, limits, zeroOffest]);

  return (
    <svg
      
      width={diameter}
      height={diameter}
      viewBox={`0 0 ${diameter} ${diameter}`}
      overflow={"visible"}
      className={""}
    >
      {limits.sweepAngle >= 2 * Math.PI && (
        <circle
          ref={trackRef as RefObject<SVGCircleElement>}
          data-slot="track"
          className={cn("stroke-muted fill-none cursor-pointer", className)}
          cx={diameter / 2}
          cy={diameter / 2}
          r={diameter / 2 - trackThickness}
          strokeWidth={trackThickness}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        />
      )}
      {limits.sweepAngle < 2 * Math.PI && (
        <path
          ref={trackRef}
          data-slot="track"
          className={cn("stroke-muted fill-none cursor-pointer", className)}
          strokeWidth={trackThickness}
          strokeLinecap={trackLinecap}
          d={`
            M ${arcPath.sX} ${arcPath.sY}
            A ${arcPath.rx} ${arcPath.ry} ${zeroOffest} ${arcPath.fA} ${arcPath.fS} ${arcPath.eX} ${arcPath.eY}    
            `}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        />
      )}

      {/* Labels */}
      {labelMap.map((label, index) => (
        <text
          key={index}
          data-slot="label"
          x={label.x}
          y={label.y}
          textAnchor="middle"
          dominantBaseline={"middle"}
          className={cn(
            "cursor-pointer select-none transition-opacity pointer-events-none fill-foreground",
            className
          )}
          style={{ opacity: label.visible ? 1 : 0 }}
        >
          {label.label}
        </text>
      ))}
    </svg>
  );
};

interface HandleProps {
  handleRef: RefObject<SVGSVGElement>
  diameter: number;
  trackThickness: number;
  zeroOffest: number;
  value: number;
  handleSize: number;
  className: string;
  isDragging: boolean
  setIsDragging: Dispatch<React.SetStateAction<boolean>>
}
const Handle = ({
  handleRef,
  diameter,
  trackThickness,
  zeroOffest,
  value,
  handleSize,
  className,
  isDragging,
  setIsDragging,
}: HandleProps) => {
  const handlePosition = useMemo(() => {
    return {
      x:
        diameter / 2 +
        (diameter / 2 - trackThickness) * Math.cos(value + zeroOffest),
      y:
        diameter / 2 +
        (diameter / 2 - trackThickness) * Math.sin(value + zeroOffest),
    };
  }, [value, diameter, trackThickness]);

  const onMouseDown = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    setIsDragging(true)
  }
  const onTouchStart = (event: React.TouchEvent<SVGSVGElement>) => {
    setIsDragging(true)
  }

  if (!handlePosition) return <></>;
  return (
    <div
      
      style={{
        transform: `translate(${handlePosition.x -
          handleSize / 2}px, ${handlePosition.y - handleSize / 2}px)`,
        position: "absolute",
        left: `0px`,
        top: `0px`,
        zIndex: 3,
      }}
    >
      <svg
        ref={handleRef}
        data-slot="handle"
        data-handle-dragging={isDragging}
        className={cn("stroke-primary fill-primary hover:scale-110 data-[handle-dragging=true]:scale-105 transition cursor-grab data-[handle-dragging=true]:cursor-grabbing", className)}
        width={handleSize}
        height={handleSize}
        viewBox={`0 0 ${handleSize} ${handleSize}`}
        style={{ overflow: "visible" }}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
      >
        <circle
          cx={handleSize / 2}
          cy={handleSize / 2}
          r={handleSize / 2}
          
        />
      </svg>
    </div>
  );
};

// Normalize angles to [0, 2π)
const normalizeAngle = (angle: number) => {
  const TWO_PI = 2 * Math.PI;
  return (angle % TWO_PI + TWO_PI) % TWO_PI
}

const getMinimumAngularDistance = (a: number, b: number) => {
  const diff = normalizeAngle(b - a)
  return Math.min(diff, 2 * Math.PI - diff)
}

function isAngleOnArc(target: number, startAngle: number, sweepAngle: number): boolean {
  target = normalizeAngle(target);
  startAngle = normalizeAngle(startAngle);
  const endAngle = normalizeAngle(startAngle + sweepAngle);
  // Determine if we are sweeping clockwise or counterclockwise
  if (sweepAngle >= 0) {
    // Clockwise arc (normal case)
    if (startAngle <= endAngle) {
      return target >= startAngle && target <= endAngle;
    } else {
      // Arc crosses 0 (e.g., 5π/3 to π/3)
      return target >= startAngle || target <= endAngle;
    }
  } else {
    // CounterClockwise arc (negative sweep)
    if (startAngle >= endAngle) {
      return target <= startAngle && target >= endAngle;
    } else {
      // Arc crosses 0 in the reverse direction
      return target <= startAngle || target >= endAngle;
    }
  }
}

const getClosestLimitAngle = (angle: number, start: number, end: number) => {
  const distToStart = getMinimumAngularDistance(angle, start)
  const distToEnd = getMinimumAngularDistance(angle, end)
  return Math.abs(distToStart) < Math.abs(distToEnd) ? start : end
}
